import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import { FormEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcherComponent } from '../components/language-switcher/language-switcher.component';
import { useRequest } from '../hooks/useRequest';
import { useAppDispatch } from '../store/hooks';
import { login } from '../store/slices/auth-slice';

export function LoginPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { fetch, isLoading, exception } = useRequest();
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedToken = String(
        new FormData(event.currentTarget).get('password') ?? '',
      ).trim();
      if (!trimmedToken) {
        setValidationError(t('login.errors.required'));
        return;
      }

      setValidationError(null);

      await fetch(() => dispatch(login(trimmedToken)).unwrap());
    },
    [dispatch, fetch, t],
  );

  return (
    <Box
      sx={{
        minHeight: '100%',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <LanguageSwitcherComponent />
      </Box>
      <Paper
        component="form"
        method="post"
        autoComplete="on"
        onSubmit={handleSubmit}
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 400,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ textAlign: 'center' }}>
          {t('login.title')}
        </Typography>
        <Box
          component="input"
          type="text"
          name="username"
          id="username"
          autoComplete="username"
          defaultValue="token"
          tabIndex={-1}
          aria-hidden
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            p: 0,
            m: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        />
        <TextField
          type="password"
          name="password"
          id="current-password"
          label={t('login.token')}
          onChange={() => {
            if (validationError) {
              setValidationError(null);
            }
          }}
          slotProps={{
            htmlInput: {
              autoComplete: 'current-password',
              autoCorrect: 'off',
              autoCapitalize: 'none',
              spellCheck: false,
            },
          }}
          error={Boolean(validationError ?? exception)}
          helperText={
            validationError ??
            (exception ? t(`exceptions.${exception}`) : undefined)
          }
          disabled={isLoading}
          autoFocus
          fullWidth
          autoComplete="current-password"
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isLoading}
        >
          {t('login.submit')}
        </Button>
      </Paper>
    </Box>
  );
}
